/**
 * Reine Ableitungen für die „Gespräch"-Karte (`ui/auftrag/AuftragGespraechKarte.tsx`), die auf der
 * Vorgangsseite die bisherige „Anfrage"-Karte ersetzt (Antoines Mockup `mockup-gespraech.html`,
 * Punkt „Gast links, STORIA rechts, voller Text, Signatur/Zitat einklappbar, echte Sendezeit").
 *
 * Zwei unabhängige, reine Funktionen:
 *  1. `gliedereGespraechNachricht` — klappt Signatur (Grußformel + Name, danach Rest) und zitierte
 *     frühere Nachrichten (`>`-Zeilen, „Am … schrieb …:", „-----Original Message-----"/
 *     „-----Ursprüngliche Nachricht-----", Outlook-Block „Von: … / Gesendet: …") automatisch weg,
 *     OHNE je Inhalt zu verwerfen — beides bleibt als String erhalten, nur `sichtbar` wird gezeigt.
 *     Bewusst konservativ (Echter Testfall unten, BFV-Anfrage 26.09.2026): eine Grußformel ohne
 *     erkennbaren Namen direkt danach oder ohne jeden weiteren Text dahinter klappt NICHTS ein
 *     ("im Zweifel lieber nicht einklappen", Vorgabe).
 *  2. `sendezeitpunkt`/`sortiereNachSendezeit` — echtes Sendedatum statt Abholzeit: der
 *     `Date`-Header aus `emails.raw_headers` (key `"date"`, lowercase — `postal-mime` liefert
 *     Header-Keys immer klein, s. `apps/api/src/lib/inbound/mime-parser.ts`), Fallback
 *     `receivedAt`/`createdAt`. Beleg (WARTESCHLANGE, 26.09.2026): die BFV-Weihnachtsfeier-Anfrage
 *     wurde am 25.09. ca. 12:30 gesendet, aber erst am 26.09. 03:10 per Postfach-Abholung
 *     (`receivedAt`) eingesammelt — ohne diese Funktion stünde sie im Gespräch HINTER unserer
 *     eigenen, sofort gesendeten Antwort.
 */

/** Minimale Form, die beide Funktionen brauchen — absichtlich kein Import aus `pages/event-detail.tsx`
 *  (kein Seiten-Import in `lib/*`, gleiches Prinzip wie `AuftragVerlaufEmailRow`). */
export interface GespraechZeitQuelle {
  rawHeaders?: unknown;
  receivedAt: string;
  createdAt?: string | null;
}

/** Ermittelt das echte Sendedatum einer Mail als ISO-String. `rawHeaders` ist `jsonb` (Migration
 *  317) — ein Objekt mit Header-Namen als Schlüssel, KLEIN geschrieben (`postal-mime`). Der
 *  einfache JSON-Webhook (`routes/inbound-mail.ts`, Provider ohne MIME-Parsing) liefert `headers`
 *  ggf. gar nicht oder mit anderer Schreibweise — dann bleibt der Fallback. */
export function sendezeitpunkt(email: GespraechZeitQuelle): string {
  const headers = email.rawHeaders;
  if (headers && typeof headers === "object") {
    const dateHeader = (headers as Record<string, unknown>).date ?? (headers as Record<string, unknown>).Date;
    if (typeof dateHeader === "string" && dateHeader.trim()) {
      const parsed = new Date(dateHeader);
      if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    }
  }
  return email.receivedAt || email.createdAt || new Date(0).toISOString();
}

/** Aufsteigend nach echter Sendezeit (älteste zuerst — Chat-Reihenfolge von oben nach unten). */
export function sortiereNachSendezeit<T extends GespraechZeitQuelle>(emails: T[]): T[] {
  return [...emails].sort((a, b) => sendezeitpunkt(a).localeCompare(sendezeitpunkt(b)));
}

export interface GespraechAbschnitt {
  text: string;
  /** Anzahl NICHT-leerer Zeilen — die Zahl im Aufklapp-Knopf ("… anzeigen (N Zeilen)"). */
  zeilen: number;
}

export interface GespraechGliederung {
  /** Immer sichtbar, ungekürzt. */
  sichtbar: string;
  /** Signatur/Rechtshinweis nach der Grußformel — `null`, wenn nichts danach folgt. */
  signatur: GespraechAbschnitt | null;
  /** Zitierte frühere Nachricht (inkl. ihrer eigenen Kopfzeile, z. B. „Am … schrieb …:") —
   *  `null`, wenn kein Zitat-Marker gefunden wurde. */
  zitat: GespraechAbschnitt | null;
}

/** Deutsche Grußformeln, mit denen eine Mail üblicherweise endet — danach folgt der Name des
 *  Absenders (Antoines Testfall: „Mit freundlichen Grüßen\nGabi Metternich"). */
const GRUSSFORMEL = /^(mit freundlichen gr[üu]ßen|herzliche gr[üu]ße|viele gr[üu]ße|beste gr[üu]ße|liebe gr[üu]ße|freundliche gr[üu]ße|mfg)[,!.]?$/i;

const ORIGINAL_MESSAGE_TRENNER = /^-{3,}.*(original message|urspr[üu]ngliche nachricht).*-{3,}$/i;
const AM_SCHRIEB = /^am\s.+\sschrieb\s.*:$/i;
const GESENDET_ZEILE = /^gesendet:\s/i;
const VON_ZEILE = /^von:\s/i;

/** Erkennt, ob Zeile `i` der Beginn einer zitierten früheren Nachricht ist (s. Modul-Kommentar für
 *  die vier Muster). Der Outlook-Block „Von: … / Gesendet: …" beginnt bei „Von:", die zugehörige
 *  „Gesendet:"-Zeile darf bis zu drei Zeilen weiter unten stehen (dazwischen z. B. „An:"). */
function istZitatStart(zeilen: string[], i: number): boolean {
  const zeile = zeilen[i]!.trim();
  if (zeile.startsWith(">")) return true;
  if (ORIGINAL_MESSAGE_TRENNER.test(zeile)) return true;
  if (AM_SCHRIEB.test(zeile)) return true;
  if (VON_ZEILE.test(zeile)) {
    for (let j = i + 1; j < Math.min(zeilen.length, i + 5); j++) {
      if (GESENDET_ZEILE.test(zeilen[j]!.trim())) return true;
    }
  }
  return false;
}

function zeilenText(zeilen: string[]): string {
  return zeilen.join("\n").replace(/^\n+|\n+$/g, "");
}

function nichtLeereAnzahl(zeilen: string[]): number {
  return zeilen.filter((z) => z.trim() !== "").length;
}

function abschnitt(zeilen: string[]): GespraechAbschnitt | null {
  const anzahl = nichtLeereAnzahl(zeilen);
  return anzahl > 0 ? { text: zeilenText(zeilen), zeilen: anzahl } : null;
}

/** Gliedert einen Mailtext in sichtbaren Teil, Signatur und Zitat (s. Modul-Kommentar). */
export function gliedereGespraechNachricht(text: string | null | undefined): GespraechGliederung {
  const zeilen = (text ?? "").replace(/\r\n/g, "\n").split("\n");

  let zitatIdx = -1;
  for (let i = 0; i < zeilen.length; i++) {
    if (istZitatStart(zeilen, i)) {
      zitatIdx = i;
      break;
    }
  }
  const vorZitat = zitatIdx === -1 ? zeilen : zeilen.slice(0, zitatIdx);

  // Grußformel suchen, dann die naechste NICHT-leere Zeile (der Name) noch mit anzeigen — erst
  // danach beginnt die einklappbare Signatur. Ohne folgende Zeile (Gruss ist das Textende) oder
  // ohne jeden weiteren Inhalt danach bleibt `signatur` unten `null` (nichts zum Einklappen).
  let sigStart = -1;
  for (let i = 0; i < vorZitat.length; i++) {
    if (GRUSSFORMEL.test(vorZitat[i]!.trim())) {
      let j = i + 1;
      while (j < vorZitat.length && vorZitat[j]!.trim() === "") j++;
      sigStart = j < vorZitat.length ? j + 1 : vorZitat.length;
      break;
    }
  }

  const sichtbarZeilen = sigStart === -1 ? vorZitat : vorZitat.slice(0, sigStart);
  const signaturZeilen = sigStart === -1 ? [] : vorZitat.slice(sigStart);
  const zitatZeilen = zitatIdx === -1 ? [] : zeilen.slice(zitatIdx);

  return {
    sichtbar: zeilenText(sichtbarZeilen),
    signatur: abschnitt(signaturZeilen),
    zitat: abschnitt(zitatZeilen),
  };
}
